import { useQuery } from "@apollo/client";
import { useCallback, useMemo, useRef } from "react";

import fetchMoreUpdateQueryPreviousFix from "apollo/fetchMoreUpdateQueryPreviousFix";
import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { Policy, SearchPoliciesOutput } from "types/generated";
import { uniqByKey } from "utils/uniq";

import {
  ENTITIES_PER_PAGE,
  EXCLUDE_LOGIN_PREDICATE,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "./constants";
import { SEARCH_POLICIES } from "./gql";

const useSearchPolicies = () => {
  const { onError } = useTypedContext(FlashContext);
  const cachedEdges = useRef<Policy[]>([]);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [urlParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams, true);

    return [...(predicatesMap?.values() || []), EXCLUDE_LOGIN_PREDICATE];
  }, [urlParams]);

  const { error, loading, data, previousData, fetchMore } = useQuery<{
    searchPolicies: SearchPoliciesOutput;
  }>(SEARCH_POLICIES, {
    variables: {
      input: {
        predicates,
        fullTextSearch: searchInput,
        first: ENTITIES_PER_PAGE,
        after: null,
        ...(sortOptionFields && { orderBy: sortOptionFields }),
      },
    },
    nextFetchPolicy: "cache-first",
  });

  const rawSearchTemplates = useMemo(() => {
    const dataPolicies = data?.searchPolicies || previousData?.searchPolicies;

    return dataPolicies;
  }, [data?.searchPolicies, previousData?.searchPolicies]);

  const memoizedEntities = useMemo(() => {
    const sourceEdges = rawSearchTemplates?.edges.map((edge) => edge.node) || [];
    const edges = loading && !sourceEdges.length ? cachedEdges.current : sourceEdges;

    if (!loading) {
      cachedEdges.current = sourceEdges;
    }

    return edges;
  }, [rawSearchTemplates?.edges, loading]);

  const isItemLoaded = useCallback(
    (value: number) => value < memoizedEntities.length,
    [memoizedEntities.length]
  );

  const loadMoreItems = async () => {
    try {
      if (data?.searchPolicies?.pageInfo.endCursor && data?.searchPolicies?.pageInfo.hasNextPage) {
        await fetchMore({
          updateQuery: (prev, { fetchMoreResult }) => {
            const previousData = fetchMoreUpdateQueryPreviousFix(
              !!prev?.searchPolicies,
              prev,
              data
            );

            if (!fetchMoreResult || !fetchMoreResult?.searchPolicies) return previousData;

            const result = fetchMoreResult?.searchPolicies;

            if (!result || !result?.edges?.length) return previousData;

            const edges = uniqByKey(
              [...(previousData?.searchPolicies?.edges || []), ...result.edges],
              "cursor"
            );

            return {
              searchPolicies: {
                ...result,
                edges,
              },
            };
          },
          variables: {
            input: {
              first: ENTITIES_PER_PAGE,
              after: data.searchPolicies.pageInfo.endCursor,
              fullTextSearch: searchInput,
              predicates,
              ...(sortOptionFields && { orderBy: sortOptionFields }),
            },
          },
        });
      }
    } catch (error) {
      onError(error);
    }
  };

  return {
    isItemLoaded,
    loadMoreItems,
    entities: memoizedEntities,
    error,
    loading,
    isPageEmpty: !!(data && !memoizedEntities.length && !searchInput && predicates.length === 0),
    hasNoFilteringResults:
      !!data && !memoizedEntities.length && (!!searchInput || predicates.length > 0),
  };
};

export default useSearchPolicies;
